import {
  isSigningErrorCode,
  type OneAuthCandidateError,
  type OneAuthErrorDetails,
  type OneAuthResultError,
  type SigningErrorCode,
} from "./types";

/** Return true only for plain records that are safe to inspect field-by-field. */
function isPlainRecord(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

/** Copy one candidate failure without leaking arbitrary provider context. */
function normalizeCandidateError(value: unknown): OneAuthCandidateError | undefined {
  if (!isPlainRecord(value)) return undefined;
  const candidate: OneAuthCandidateError = {};
  if (typeof value.code === "string") candidate.code = value.code;
  if (typeof value.message === "string") candidate.message = value.message;
  const details = normalizeOneAuthErrorDetails(value.details);
  if (details) candidate.details = details;
  return Object.keys(candidate).length > 0 ? candidate : undefined;
}

/**
 * Normalizes untrusted provider diagnostics to the declared public allowlist.
 * Strings from prepare endpoints are retained as a reason, while arrays are
 * exposed only as sanitized candidate errors rather than cast to an object.
 */
export function normalizeOneAuthErrorDetails(value: unknown): OneAuthErrorDetails | undefined {
  if (typeof value === "string") return { reason: value };
  if (Array.isArray(value)) {
    const candidateErrors = value
      .map(normalizeCandidateError)
      .filter((candidate): candidate is OneAuthCandidateError => candidate !== undefined);
    return candidateErrors.length > 0 ? { candidateErrors } : undefined;
  }
  if (!isPlainRecord(value)) return undefined;

  const details: OneAuthErrorDetails = {};
  if (typeof value.providerCode === "string") details.providerCode = value.providerCode;
  if (typeof value.traceId === "string") details.traceId = value.traceId;
  if (typeof value.statusCode === "number" && Number.isFinite(value.statusCode)) {
    details.statusCode = value.statusCode;
  }
  if (typeof value.errorType === "string") details.errorType = value.errorType;
  if (Array.isArray(value.simulationUrls)) {
    const simulationUrls = value.simulationUrls.filter(
      (url): url is string => typeof url === "string",
    );
    if (simulationUrls.length > 0) details.simulationUrls = simulationUrls;
  }
  if (typeof value.reason === "string") details.reason = value.reason;
  if (Array.isArray(value.candidateErrors)) {
    const candidateErrors = value.candidateErrors
      .map(normalizeCandidateError)
      .filter((candidate): candidate is OneAuthCandidateError => candidate !== undefined);
    if (candidateErrors.length > 0) details.candidateErrors = candidateErrors;
  }
  return Object.keys(details).length > 0 ? details : undefined;
}

/**
 * Parses an untrusted signing error without allowing its code to widen the
 * public SigningErrorCode contract.
 */
export function parseSigningResultError(
  value: unknown,
  fallbackCode: SigningErrorCode = "SIGNING_FAILED",
  fallbackMessage = "Signing failed",
): OneAuthResultError<SigningErrorCode> {
  if (!isPlainRecord(value)) return { code: fallbackCode, message: fallbackMessage };
  const code = isSigningErrorCode(value.code) ? value.code : value.code === undefined ? fallbackCode : "UNKNOWN";
  return {
    code,
    message: typeof value.message === "string" ? value.message : fallbackMessage,
    details: normalizeOneAuthErrorDetails(value.details),
  };
}

/**
 * Error thrown by SDK adapters that must use exception-based APIs, such as
 * EIP-1193, viem accounts, wallet clients, and React callbacks.
 *
 * `code` is stable enough for application branching. `details` contains
 * provider/orchestrator diagnostics such as trace IDs and simulation URLs.
 */
export class OneAuthError extends Error {
  readonly code: string;
  readonly details?: OneAuthErrorDetails;

  constructor(code: string, message: string, details?: OneAuthErrorDetails) {
    super(message);
    this.name = "OneAuthError";
    this.code = code;
    this.details = details;
  }
}

/**
 * Converts a result-object error into the SDK's structured exception type.
 */
export function toOneAuthError(
  error: { code?: string; message?: string; details?: unknown } | undefined,
  fallbackMessage: string,
  fallbackCode = "UNKNOWN",
): OneAuthError {
  return new OneAuthError(
    error?.code || fallbackCode,
    error?.message || fallbackMessage,
    normalizeOneAuthErrorDetails(error?.details),
  );
}

/**
 * Maps an auth-service execute response to the stable public SDK stage code.
 * Provider-specific codes belong in `details.providerCode`; the invalid
 * signature exception remains public because applications must route recovery.
 */
export function executeErrorCode(errorData: unknown): "INVALID_SIGNATURE" | "EXECUTE_FAILED" {
  if (!errorData || typeof errorData !== "object" || !("code" in errorData)) {
    return "EXECUTE_FAILED";
  }
  return errorData.code === "INVALID_SIGNATURE" ? "INVALID_SIGNATURE" : "EXECUTE_FAILED";
}

/**
 * Normalizes support-safe auth-service diagnostics to the public SDK shape.
 * The service names its upstream code `code`; SDK consumers receive it as
 * `providerCode` so it cannot be confused with the public stage code.
 */
export function executeErrorDetails(debug: unknown): OneAuthErrorDetails | undefined {
  if (!debug || typeof debug !== "object") return undefined;
  const details: OneAuthErrorDetails = {};
  if ("code" in debug && typeof debug.code === "string") details.providerCode = debug.code;
  if ("traceId" in debug && typeof debug.traceId === "string") details.traceId = debug.traceId;
  if ("statusCode" in debug && typeof debug.statusCode === "number") {
    details.statusCode = debug.statusCode;
  }
  if ("errorType" in debug && typeof debug.errorType === "string") {
    details.errorType = debug.errorType;
  }
  if ("simulationUrls" in debug && Array.isArray(debug.simulationUrls)) {
    details.simulationUrls = debug.simulationUrls.filter(
      (url): url is string => typeof url === "string",
    );
  }
  return Object.keys(details).length > 0 ? details : undefined;
}
